'use client';
import React from 'react';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useBenzingaEdge } from '@benzinga/edge';
import { ErrorBoundary } from '@benzinga/core-ui';
import { useScrollDepthTracking } from '@benzinga/analytics';
import { QuoteHeader, type QuoteHeaderProps } from './QuoteHeader';
import { useFirstInteractionMade } from '@benzinga/hooks';
import { deriveQuoteActiveTabFromPathname } from '../../utils/quote/active-tab';
import styles from './QuoteLayout.module.scss';

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

const WNSTNWidgetQuote = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.WNSTNWidgetQuote })),
);

interface QuoteLayoutProps extends Omit<QuoteHeaderProps, 'activeTab'> {
  symbol: string;
}

export const QuoteLayout: React.FC<React.PropsWithChildren<QuoteLayoutProps>> = ({ children, profile, symbol }) => {
  const hasAdLight = useBenzingaEdge().adLightEnabled;
  const hasUserInteracted = useFirstInteractionMade();
  const pathname = usePathname();
  const activeTab = deriveQuoteActiveTabFromPathname(pathname);

  useScrollDepthTracking();

  return (
    <div className={classNames(styles.quoteLayoutWrapper, 'quote-page-container', activeTab)}>
      <QuoteHeader activeTab={activeTab} profile={profile} symbol={symbol} />
      {!hasAdLight ? (
        <div className="quote-page-above-layout-ads">
          <FreestarAd onlyMobile={true} placement="incontent" />
        </div>
      ) : null}
      <div className={classNames('flex flex-col gap-y-8 w-full ml-auto py-4')}>{children}</div>
      <ErrorBoundary name="floating-wnstn-widget">
        {hasUserInteracted ? (
          <React.Suspense fallback={null}>
            <WNSTNWidgetQuote richQuoteData={profile?.richQuoteData} symbol={symbol} />
          </React.Suspense>
        ) : null}
      </ErrorBoundary>
    </div>
  );
};
